import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { search } from './api';
import { searchKeys } from './keys';

export { searchKeys };
import type { SearchQueryRequest } from './types/request';

export type SearchError = {
  status: number;
  detail: string;
};

export type UseSearchInfiniteQueryParams = Omit<SearchQueryRequest, 'limit' | 'offset'> & {
  pageSize?: number;
};

/** Дополнительные параметры запроса поиска. */
export type UseSearchQueryOptions = {
  /** Не выполнять запрос, пока спрашивать сервер не о чем (список строится локально) */
  enabled?: boolean;
};

/**
 * Generic search query hook. Runs when request changes.
 */
export const useSearchQuery = <T = unknown>(request: SearchQueryRequest, options?: UseSearchQueryOptions) => {
  return useQuery({
    queryKey: searchKeys.query(request),
    queryFn: () => search<T>(request),
    retry: false,
    placeholderData: keepPreviousData,
    enabled: options?.enabled ?? true,
  });
};

/**
 * Generic infinite search query hook.
 */
export const useSearchInfiniteQuery = <T = unknown>(params: UseSearchInfiniteQueryParams) => {
  const pageSize = params.pageSize ?? 50;

  return useInfiniteQuery({
    queryKey: searchKeys.infiniteQuery({ ...params, pageSize }),
    queryFn: ({ pageParam }) =>
      search<T>({
        ...params,
        limit: pageSize,
        offset: (pageParam as number) * pageSize,
      } as SearchQueryRequest),
    initialPageParam: 0,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      const nextOffset = ((lastPageParam as number) + 1) * pageSize;
      return lastPage.total > nextOffset ? lastPageParam + 1 : undefined;
    },
    placeholderData: keepPreviousData,
    retry: false,
  });
};
